import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Activity,
  Heart,
  Zap,
  AlertTriangle,
  Sparkles,
  Loader2,
  CheckCircle2,
  TrendingDown,
  TrendingUp
} from 'lucide-react';

import { API_URL } from '../config';

export default function UrgRythmologiePage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (protocolId, protocolName, content) => {
    setAiLoading({ ...aiLoading, [protocolId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'urgences_rythmologie',
          audience: 'professional',
          prompt: `Analyse rythmologique pour ${protocolName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Reconnaissance du trouble du rythme
2. Évaluation de la tolérance hémodynamique
3. Arbre décisionnel thérapeutique
4. Doses médicamenteuses précises
5. Critères de cardioversion électrique`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [protocolId]: data.response });
        toast.success(`Analyse AI ${protocolName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [protocolId]: false });
    }
  };

  const protocols = [
    {
      id: 'troubles-conduction',
      name: 'Troubles de la Conduction',
      icon: Activity,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-400/30',
      content: `**TROUBLES DE LA CONDUCTION**

**A. BSA (Bloc Sino-Auriculaire)**

**BSA I**
• P-P constants
• Bloc non détectable

**BSA II – Type I croissant**
• P-P croissant jusqu'à pause

**BSA II – Type I décroissant**
• P-P décroissant jusqu'à pause

**BSA II – Type II**
• P-P constants
• Une seule pause

**BSA II – haut degré**
• P-P constants
• ≥ 2 pauses

**BSA III**
• P absente
• Rythme d'échappement

⸻

**B. BAV (Bloc Auriculo-Ventriculaire)**

**BAV I**
• P-R constant ≥ 200 ms

**BAV II – Mobitz I (Wenckebach)**
• P-R croissant jusqu'à P bloquée

**BAV 2/1**
• P-R constant
• ratio P/QRS = 2

**BAV II – Mobitz II**
• P-R constant
• P bloquée parfois

**BAV haut degré**
• PP réguliers
• ≥ 2 P bloquées

**BAV III**
• Dissociation entre P et complexes QRS`
    },
    {
      id: 'acr-adulte',
      name: 'ACR Adulte',
      icon: Heart,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**ARRÊT CARDIO-RESPIRATOIRE ADULTE**

**RECONNAISSANCE**

• Patient inconscient
• Absence de respiration normale
• Absence de pouls carotidien palpable

**ALGORITHME DE RÉANIMATION**

**1. APPELER À L'AIDE + SCOPE**

**2. MASSAGE CARDIAQUE EXTERNE (MCE)**
• **Fréquence:** 100-120 compressions/min
• **Profondeur:** 5-6 cm
• **Décompression complète**
• **Ratio 30:2** (compressions:ventilations)
• **Minimiser les interruptions**

**3. ANALYSE DU RYTHME**

**RYTHMES CHOQUABLES:**

**Fibrillation Ventriculaire (FV) / Tachycardie Ventriculaire sans pouls (TV):**

• **Choc électrique externe (CEE):**
  - 1er choc: **200 J** (biphasique)
  - 360 J (monophasique)
• **Reprendre MCE immédiatement** (2 min)
• **Adrénaline:**
  - Après 3ème choc
  - **1 mg IV/IO toutes les 3-5 min**
• **Amiodarone:**
  - Après 3ème choc: **300 mg IV/IO**
  - Après 5ème choc: **150 mg IV/IO**

**RYTHMES NON CHOQUABLES:**

**Asystolie / Activité Électrique sans Pouls (AESP):**

• **MCE de haute qualité**
• **Adrénaline:**
  - Dès que possible
  - **1 mg IV/IO toutes les 3-5 min**
• **Rechercher et traiter causes réversibles (4H + 4T)**

**4H - CAUSES RÉVERSIBLES:**
• **Hypoxie** → Oxygénation, ventilation
• **Hypovolémie** → Remplissage vasculaire
• **Hyper/Hypokaliémie** → Correction électrolytique
• **Hypothermie** → Réchauffement

**4T - CAUSES RÉVERSIBLES:**
• **Thrombose coronaire** → Fibrinolyse, angioplastie
• **Thrombose pulmonaire** → Fibrinolyse
• **Tamponnade** → Péricardiocentèse
• **Tension pneumothorax** → Exsufflation, drainage

**VOIES AÉRIENNES**

• **Oxygénation:** FiO2 100%
• **Ventilation:** BAVU puis intubation
• **CapnoCo2:** Objectif > 10 mmHg (bon MCE)

**ACCÈS VASCULAIRE**

• **Voie veineuse périphérique** (1ère intention)
• **Voie intra-osseuse** si échec VVP
• **Flush 20 mL NaCl 0,9%** après chaque médicament

**SURVEILLANCE POST-RACS**

**Si Reprise d'Activité Cardiaque Spontanée (RACS):**

• **Maintenir saturation 94-98%**
• **Éviter hyperoxie**
• **PAM > 65 mmHg** (vasopresseurs si besoin)
• **Glycémie 6-10 mmol/L**
• **Température 32-36°C** (hypothermie thérapeutique si indiquée)
• **ECG 18 dérivations**
• **Transfert vers réanimation/USIC**
• **Coronarographie en urgence** si suspicion SCA ST+

**CRITÈRES D'ARRÊT DE LA RCP**

• RACS obtenue
• Épuisement des sauveteurs
• Situation manifestement désespérée
• Décision médicale après 30 min RCP bien conduite sans RACS

**POINTS CLÉS**

✓ MCE de haute qualité = priorité absolue
✓ Minimiser interruptions MCE (< 10 sec)
✓ Choc + MCE immédiat si FV/TV
✓ Adrénaline précoce si asystolie/AESP
✓ Rechercher et traiter 4H + 4T
✓ Capnographie pour guider qualité MCE`
    },
    {
      id: 'bradycardie',
      name: 'Bradycardie Adulte & Pédiatrie',
      icon: TrendingDown,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**BRADYCARDIE ADULTE & ENFANT**

**MISE EN CONDITION**

• **ECG 18D + D2 long**
• **Scope, PA, SpO2**
• **Température**
• **VVP**
• **Traitement étiologique:**
  - SCA, hyperkaliémie, hypothyroïdie
  - Myocardite, troubles ioniques, hypothermie
• **Cause iatrogène:**
  - β-bloquant, inhibiteur calcique, digitalique
  - ⚠ **Si intoxication médicamenteuse** → cf protocole spécifique

**ÉVALUATION DE LA TOLÉRANCE**

**SIGNES DE MAUVAISE TOLÉRANCE:**
• **FC < 35 BPM** / Torsade de Pointe
• **Insuffisance cardiaque**
• **Troubles de la conscience**

**SI SIGNES DE MAUVAISE TOLÉRANCE → OUI**

**ÉTAPE 1: ATROPINE**

**Utilisation:**
• **PURE (aucune CI si détresse vitale)**
• **CI relatives:**
  - Allaitement, RAU
  - Glaucome à angle fermé de l'angle

**Posologie ADULTE:**
• **0,5 mg IVD** toutes les 3-5 minutes
• **Maximum 3 mg**

**Posologie PÉDIATRIE:**
• **20 µg/kg IVD**
• Minimum 100 µg
• Maximum 500 µg & 1 mg/24h

**SI ÉCHEC ATROPINE**

**ÉTAPE 2: ISOPRÉNALINE**

**Utilisation:**
• **1 mg (5ml)** dans seringue de **50 ml**
• Complétée avec **G5%** ou **NaCl 0,9%**
• Seringue et tubulure opaque

**CI:**
• **SCA** (privilégier EESE)
• **RAC, Hyperthyroïdie**
• **Intox aux digitaliques, HypoK+**

**Posologie:**
• **CI: SCA** privilégier EESE, RAC, Hyperthyroïdie, intox aux digitaliques, HypoK+

**FC ventriculaire CIBLE: 60 BPM**

**INTENSITÉ:**
• **Initiale 40-50 mA**
• Augmenter de 10 en 10 jusqu'à ce que les spikes soient tous suivis d'un QRS
• **Max 120 mA**
• Si mauvaise tolérance: Analgésie/sédation

**SCA:**
• Si EESE mal tolérée:
  - **DOBUTAMINE 5 µg/kg/min** (cible 50 BPM)

**SI ÉCHEC OU ALTERNATIVE EESE**

**ÉTAPE 3: EESE (Entrainement Électro-Systolique Externe)**

**Modalités:**
• Position patches: antéro-postérieur ou antéro-latéral
• Mode: demande
• Fréquence: selon âge et contexte

**EXCEPTIONNEL À DISCUTER:**

**ADRÉNALINE IVSE 2 à 10 µg/min**

**SI BONNE TOLÉRANCE → SURVEILLANCE**

• **Atropine prête**
• Monitoring continu
• Évaluation répétée

**PÉDIATRIE - PARTICULARITÉS**

**Seuils de bradycardie pathologique:**
• **Nouveau-né:** < 100 bpm
• **Nourrisson (< 1 an):** < 80 bpm
• **Enfant 1-3 ans:** < 70 bpm
• **Enfant 3-12 ans:** < 60 bpm
• **Adolescent:** < 50 bpm

**Atropine pédiatrie:**
• **20 µg/kg IVD**
• Min 100 µg, max 500 µg

**PED: Si conscient SÉDATION**
• **KÉTAMINE 1 mg/kg**
• Puis CEE **1J/kg**, si échec **2J/kg**
• Puis après avis **4J/kg**

**POINTS CLÉS**

✓ Évaluer tolérance hémodynamique
✓ Atropine en 1ère intention si mal toléré
✓ Isoprénaline si échec (sauf SCA)
✓ EESE en dernier recours
✓ Traiter la cause sous-jacente`
    },
    {
      id: 'algo-tachycardie',
      name: 'Algorithme Tachycardie Adulte & Pédiatrie',
      icon: TrendingUp,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**ALGORITHME TACHYCARDIE ADULTE & ENFANT**

**DIAGNOSTIC INITIAL**

• **ECG 18 dérivations** DIAGNOSTIC + **D2 long**
• **Scope ECG, PNI, SpO2**
• **Examen clinique** (IC etc.)

**ÉVALUATION DE LA TOLÉRANCE**

**BONNE TOLÉRANCE:**
• Pas d'insuffisance cardiaque
• Pas de douleur angineuse
• Bonne hémodynamique

→ **SURVEILLANCE**
→ Discuter explorations complémentaires
→ Notamment si TC avec des **QRS larges non connus**

**TOLÉRANCE MODÉRÉE:**
• Insuffisance cardiaque
• Douleur angineuse/SCA
• Hypotension

→ **QRS FIN?** (< 120ms)

**SI QRS FIN:**
→ Cf. Tachycardie à QRS fin modérément tolérée

**SI QRS LARGE:**
→ Cf. Tachycardie à QRS large modérément tolérée

**MAUVAISE TOLÉRANCE:**
• Perte de connaissance
• Choc
• Détresse respiratoire

→ **CARDIOVERSION ÉLECTRIQUE EN URGENCE**

**PROCÉDURE DE CARDIOVERSION D'URGENCE:**

**Si FA/Flutter:**
• Avant le geste: **LOVENOX SC 100UI/kg**
• Si conscient: **sédation**
• **PROPOFOL 0,5-0,8 mg/kg IVD**
• Puis **CEE**
• **FA/FLUTTER: 70-120J**
• **TV: 120-150J**
• Biphasique synchrone sur R
• 3x si besoin en augmentant progressivement

**Ajouter CORDARONE:**
• **300mg IVSE 10'**
• Puis **CORDARONE 900mg/24h**

**ÉCHEC/RÉCIDIVE/DÉGRADATION:**
• **ANESTHÉSIE GÉNÉRALE**
• Cf. **ISR et IOT**

**PÉDIATRIE - PARTICULARITÉS:**

**Seuils de tachycardie pathologique:**
• **Nouveau-né:** > 200 bpm
• **Nourrisson (< 1 an):** > 180 bpm
• **Enfant 1-12 ans:** > 160 bpm
• **Adolescent:** > 140 bpm

**PED: Si conscient SÉDATION:**
• **KÉTAMINE 1 mg/kg**
• Puis CEE **1J/kg**, si échec **2J/kg**
• Puis après avis **4J/kg**

**PIÈGE : SUPER WOLF = FA + WOLF PARKINSON WHITE**

**Caractéristiques ECG:**
• **"QRS en accordéon"**
• Pas d'ATP sinon passage en TV et risque d'ACR
• **FLECAÏNE:** 2 mg/kg, IVL sur 10' (attention aux CI)
• À défaut **CORDARONE:** 5mg/kg sur 20'
• Puis relais **50mg/h**
• **+ ANTICOAGULATION:** LOVENOX 100UI/kg en SC

**ATTENTION:**
• **GREFFE CARDIAQUE = CI ATROPINE**

**POINTS CLÉS**

✓ ECG 18D systématique
✓ Évaluer tolérance hémodynamique
✓ QRS fin vs large oriente traitement
✓ CEE urgente si mauvaise tolérance
✓ Attention au syndrome de WPW
✓ Sédation avant CEE si patient conscient`
    },
    {
      id: 'tachy-qrs-fins',
      name: 'Tachycardie à QRS Fins',
      icon: Zap,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**TACHYCARDIE À QRS FINS**
**MODÉRÉMENT TOLÉRÉE ADULTE & ENFANT**

**DÉFINITION:**
• Tachycardie avec QRS < 120 ms
• **En cas de DÉGRADATION** du patient (perte de connaissance, choc, DRA etc.)
• Procéder à une **RÉDUCTION ÉLECTRIQUE** du trouble du rythme devenant mal toléré
• Cf. Algorithme d'aide décisionnelle devant une tachycardie

**CLASSIFICATION**

**RÉGULIÈRE:**

**1. TACHYCARDIE SINUSALE**

**Caractéristiques ECG:**
• Rythme régulier
• Onde P visible avant chaque QRS
• Morphologie P normale

**Cause non cardiaque probable:**
• Fièvre, douleur, anxiété
• Hypovolémie, anémie
• Hyperthyroïdie
• Traiter la cause

**2. FLUTTER ATRIAL**

**Caractéristiques ECG:**
• Aspect en "toit d'usine"
• Peut manquer et être démasqué par l'ATP
• **PEC idem FA** (cf. ci-contre)

**3. TACHYCARDIE JONCTIONNELLE**

**Caractéristiques ECG:**
• Onde P rétrograde ou absente
• QRS fins réguliers

**Traitement:**
• **1- MANŒUVRES VAGALES**
• **2- SI ÉCHEC: ADÉNOSINE**
  - Injection sur un pli de coude, Kt court
  - IVD suivi d'un bolus de cristalloïde
  - Enregistrement continu ECG
  - **ATROPINE disponible**

**Au choix:**
• **KRENOSIN®:**
  - **6mg**, si échec **12mg**, si échec discuter **18mg**
  - **PED:** 1er **0,1mg/kg max 6mg**
  - Puis **0,2mg/kg max 12mg**
• **STRIADYNE®:**
  - **10mg**, si échec **20mg**
  - **PED:** **1mg/kg max 20mg**

**Attendre 1-2min entre chaque injection**

**CI:**
• Asthme/BPCO, etc.

**IRRÉGULIÈRE:**

**PIÈGE : FA + WOLF PARKINSON WHITE**

**Caractéristiques ECG:**
• "**QRS en accordéon**"

**Cf. Algorithme Tachycardie:**
• Pas d'ATP sinon passage en TV et FLECAÏNE (sauf CI)
• À défaut CORDARONE + ANTICOAGULATION CURATIVE

**Ou CEE si mauvaise tolérance**

**FIBRILLATION ATRIALE**

**Si FA de début < 48H : Cardioversion chimique**

**1- ANTICOAGULATION CURRATIVE**
• Sauf CI
• Ex : LOVENOX® : 100UI/kg en SC toutes les 12H

**2- RÉDUCTION:**
• **CORDARONE® 5mg/kg sur 1h**
• Puis, si non réduit, relais **50mg/h sur 24h**

**Si > 48H : Contrôle cadence ventriculaire**

**Exemple : DIGOXINE** (sauf CI : HypoK+ etc.) :
• **0,5 mg IVL** toutes les **4H SB max 1,5mg/24H**
• Sous surveillance scopée

**Ou BÉTABLOQUANT : ATENOLOL** sujet jeune,
**BISOPROLOL** pour les patients âgés

**ATTENTION:**
• **GREFFE CARDIAQUE = CI ATROPINE**

**POINTS CLÉS**

✓ Distinguer régulière vs irrégulière
✓ Tachycardie sinusale = traiter la cause
✓ Manœuvres vagales en 1ère intention
✓ Adénosine si échec (Krenosin, Striadyne)
✓ FA < 48h = cardioversion possible
✓ FA > 48h = contrôle fréquence
✓ Attention au syndrome de WPW`
    },
    {
      id: 'tachy-qrs-larges',
      name: 'Tachycardie à QRS Larges',
      icon: Activity,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**TACHYCARDIE À QRS LARGES**
**MODÉRÉMENT TOLÉRÉE ADULTE & ENFANT**

**DÉFINITION:**
• Tachycardie avec QRS ≥ 120 ms
• **En cas de DÉGRADATION** du patient (perte de connaissance, choc, DRA etc.)
• Procéder à une **RÉDUCTION ÉLECTRIQUE** du trouble du rythme devenant mal toléré
• Cf. Algorithme d'aide décisionnelle devant une tachycardie

**CLASSIFICATION**

**RÉGULIÈRE:**

**TSV + BLOC DE BRANCHE? TV?**
• Tenter une réduction de **TSV**

**1- MANŒUVRES VAGALES**

**2- SI ÉCHEC: ADÉNOSINE**

**Injection sur un pli de coude, Kt court,**
**IVD suivi d'un bolus de cristalloïde**
**Enregistrement continu ECG**
**ATROPINE disponible**

**Au choix:**
• **KRENOSIN®:**
  - **3mg**, si échec **6mg**, si échec **12mg**
  - **PED:** 1er **0,1mg/kg max 6mg**
  - Puis **0,2mg/kg max 12mg**

• **STRIADYNE®:**
  - **10mg**, si échec **20mg**
  - **PED:** **1mg/kg max 20mg**

**Attendre 1-2min entre chaque injection**

**CI:** Asthme/BPCO, etc.

**SI ÉCHEC malgré manifestations vagales à l'injection d'adénosine: TV PROBABLE**

**TRAITEMENT TV:**

**CORDARONE®:**
• **5mg/kg en 1h**
• Puis relais IVSE **50mg/h sur 24h**

**PED:** Privilégier l'abstention thérapeutique
• Si mauvaise tolérance → **CEE**
• **1J/kg** si échec **2J/kg**
• Sédation: **PROPOFOL** ou **KÉTAMINE 1mg/kg**

**IRRÉGULIÈRE:**

**PIÈGE : FA + WOLF PARKINSON WHITE**

**Cf. Algorithme Tachycardie:**

**Caractéristiques:**
• "**QRS en accordéon**"

**TORSADE DE POINTE**

**Caractéristiques ECG:**
• Torsade des QRS autour de la ligne isoélectrique
• Survient sur QT long

**Traitement:**

→ **SULFATE DE MAGNÉSIUM 3g sur 10'**
• Renouvelable 1 fois SB + (ex : 2g sur 2h)
• **PED:** **25-50 mg/kg en 10'** (max: 2 g)
• Renouveler à 5-10' SB puis IVSE à 3-10 mg/min

→ **SI ÉCHEC et contexte de BRADYCARDIE,**
**Traiter la bradycardie** Cf. Bradycardie

**Si mauvaise tolérance + CEE**

**Cf. Algorithme d'aide devant une tachycardie**

**FIBRILLATION AURICULAIRE + BLOC DE BRANCHE**

**Cf. TCR à QRS fins modérément tolérée**

**Sous-rubrique Fibrillation atriale**

**RIVA (Rythme Idioventriculaire Accéléré)**

**Caractéristiques:**
• **FC < 120BPM** régulière, transitoire
• Post-infarctus, reperfusion myocardique

**Traitement:**
• **Surveillance simple**
• Pas d'ATP, pas de traitement antiarythmique

**ATTENTION:**
• **GREFFE CARDIAQUE = CI ATROPINE**

**POINTS CLÉS**

✓ QRS large = présumer TV jusqu'à preuve du contraire
✓ Manœuvres vagales + Adénosine si doute TSV
✓ Cordarone si TV confirmée
✓ Sulfate de magnésium si Torsade de Pointe
✓ CEE si dégradation hémodynamique
✓ Attention au syndrome de WPW
✓ RIVA = surveillance simple, pas de traitement`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="urg-rythmo-container">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-pink-500/20 border border-red-400/30">
            <Activity className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent">
              Urg'Rythmologie
            </h1>
            <p className="text-[hsl(var(--muted-2))] mt-1">
              Protocoles urgences rythmologiques - Guides pas-à-pas interactifs
            </p>
          </div>
        </div>

        {/* Alert */}
        <Alert className="border-red-400/50 bg-red-500/10">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <AlertDescription className="text-red-300 font-medium">
            <strong>Urgences rythmologiques:</strong> Évaluer tolérance hémodynamique • ECG 18D systématique • CEE si mauvaise tolérance • Rechercher causes réversibles
          </AlertDescription>
        </Alert>
      </div>

      {/* Protocols Card */}
      <Card className="border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Heart className="w-6 h-6 text-red-400" />
            Protocoles Rythmologiques
          </CardTitle>
          <CardDescription>
            Sélectionnez un protocole pour afficher l'algorithme détaillé
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="space-y-4">
            {protocols.map((protocol) => {
              const Icon = protocol.icon;
              const isLoading = aiLoading[protocol.id];
              const aiResult = aiResults[protocol.id];

              return (
                <AccordionItem 
                  key={protocol.id} 
                  value={protocol.id}
                  className={`border ${protocol.border} ${protocol.bg} rounded-lg overflow-hidden`}
                  data-testid="protocol-item"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <div className={`p-2 rounded-lg ${protocol.bg} border ${protocol.border}`}>
                        <Icon className={`w-5 h-5 ${protocol.color}`} />
                      </div>
                      <span className="font-semibold text-lg">{protocol.name}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="px-6 pb-6 space-y-6">
                      <ScrollArea className="h-auto max-h-[600px]">
                        <div className="prose prose-invert max-w-none pr-4">
                          <div className="text-[hsl(var(--text))] space-y-4">
                            {protocol.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                const text = line.replace(/\*\*/g, '');
                                if (line.includes('RECONNAISSANCE') || line.includes('ALGORITHME') || line.includes('MISE EN CONDITION')) {
                                  return <h3 key={idx} className="text-xl font-bold text-cyan-400 mt-6 mb-3">{text}</h3>;
                                }
                                return <h4 key={idx} className="text-lg font-semibold text-cyan-300 mt-4 mb-2">{text}</h4>;
                              }
                              if (line.trim().startsWith('•')) {
                                return <li key={idx} className="ml-4 text-[hsl(var(--text))] leading-relaxed">{line.replace('•', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('-')) {
                                return <li key={idx} className="ml-8 text-[hsl(var(--muted-2))] text-sm leading-relaxed list-disc">{line.replace('-', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('✓')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-cyan-500/10 rounded-lg border border-cyan-400/30 my-2">
                                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-cyan-300 font-medium">{line.replace('✓', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim().startsWith('⚠')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-orange-300 font-medium">{line.replace('⚠', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim().startsWith('→')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-2 bg-blue-500/10 rounded border-l-4 border-blue-400 my-2">
                                    <span className="text-blue-300 font-medium">{line.replace('→', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim()) {
                                return <p key={idx} className="text-[hsl(var(--text))] leading-relaxed">{line}</p>;
                              }
                              return <div key={idx} className="h-2" />;
                            })}
                          </div>
                        </div>
                      </ScrollArea>

                      <Button
                        onClick={() => handleAiAnalysis(protocol.id, protocol.name, protocol.content)}
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                        data-testid={`ai-button-${protocol.id}`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyse AI en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Analyse AI de ce protocole
                          </>
                        )}
                      </Button>

                      {aiResult && (
                        <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                          <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Analyse AI
                          </h3>
                          <div className="prose prose-invert max-w-none">
                            <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${protocol.id}`}>
                              {aiResult}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
